import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "./api-client";

export const refundAdminKeys = {
  all: () => ["refund-admin"],
  list: (params) => ["refund-admin", "list", params],
};

/**
 * @param {{ page?: number; limit?: number }} params
 */
export function fetchAdminRefundAll({ page = 1, limit = 10 } = {}) {
  return apiClient({
    url: "admin/refund/all",
    method: "GET",
    params: { page, limit },
  });
}

/**
 * @param {string} slug
 * @param {{ status: "completed" | "rejected"; proof?: File; rejectionReason?: string }} payload
 */
export function patchAdminRefundApproval(slug, { status, proof, rejectionReason }) {
  const fd = new FormData();
  fd.append("status", status);
  if (status === "rejected") {
    fd.append("rejectionReason", String(rejectionReason ?? "").trim());
  } else if (proof) {
    fd.append("proof", proof);
  }
  return apiClient({
    url: `admin/refund/approval/${slug}`,
    method: "PATCH",
    data: fd,
  });
}

/**
 * @param {{ page?: number; limit?: number }} params
 */
export function useAdminRefundListQuery(params) {
  return useQuery({
    queryKey: refundAdminKeys.list(params),
    queryFn: () => fetchAdminRefundAll(params),
    placeholderData: (prev) => prev,
  });
}

export function useAdminRefundApprovalMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, status, proof, rejectionReason }) =>
      patchAdminRefundApproval(slug, { status, proof, rejectionReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: refundAdminKeys.all() });
    },
  });
}

export function extractAdminRefundRows(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

export function extractAdminRefundTotal(payload) {
  if (payload == null) return 0;
  const n = payload.total;
  if (typeof n === "number" && !Number.isNaN(n)) return n;
  return extractAdminRefundRows(payload).length;
}
